/**
 * Part Delta Streaming Client
 *
 * Handles real-time streaming of text content deltas during AI response
 * generation. Uses the same WebSocket connection as DAG sync but focuses
 * specifically on part deltas.
 *
 * Subscription model (2026-08-18, masterplan/28 §4): ONE `subscribe_task_deltas`
 * per open chat replaces the per-part subscribe churn — the server publishes
 * every part's deltas (and part boundaries) on a per-task Redis stream.
 * Part renderers register HANDLERS by part id; incoming deltas route by
 * `delta.part_id`. Deltas arriving before a part's renderer mounts are
 * buffered per part and replayed on handler registration. The legacy
 * per-part subscribe message remains as a fallback for callers with no
 * task context.
 */

import {
  PartDeltaEvent,
  PartStreamingSubscription,
  PartStreamingMessage,
  PartStreamingResponse
} from './types';
import { getActiveBranch } from '../active-branch';

/** The branch a delta subscription reads through: the one the page names on
 *  its HTTP requests (`x-branch`), none on main. The socket's own lane is
 *  the signed-in user's overlay on main, so a part or task that exists only
 *  in my draft on a branch was not found without it (follow-up 46). */
function boundBranch(): { branch?: string } {
  const b = getActiveBranch();
  return b ? { branch: b.branch } : {};
}

export type PartDeltaHandler = (event: PartDeltaEvent) => void;
export type PartStreamingErrorHandler = (error: string) => void;

interface TaskStreamSubscription {
  task_id: string;
  isActive: boolean;
  /** Redis stream id of the last delta seen — reconnect resume cursor. */
  lastStreamId: string;
  /** the branch it was subscribed on, kept for a resubscribe */
  branch?: string;
}

export class PartStreamingClient {
  private subscriptions = new Map<string, PartStreamingSubscription>();
  private deltaHandlers = new Map<string, PartDeltaHandler>();
  private errorHandlers = new Set<PartStreamingErrorHandler>();
  private taskSubscriptions = new Map<string, TaskStreamSubscription>();

  constructor(private websocket: WebSocket) {
    this.websocket = websocket;
  }

  // Subscribe to ALL delta events of a task (one per open chat).
  /** `branch`: the lane to read through; by default the page's bound branch
   *  (a resubscribe passes the one it was subscribed on). */
  subscribeToTaskDeltas(taskId: string, startId: string = "0", branch?: string): void {
    if (this.websocket.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket is not connected');
    }
    // Idempotent: requested-or-active subscription is not re-sent.
    if (this.taskSubscriptions.has(taskId)) {
      return;
    }
    const lane = branch !== undefined ? (branch ? { branch } : {}) : boundBranch();
    this.taskSubscriptions.set(taskId, {
      task_id: taskId,
      isActive: false,
      lastStreamId: startId,
      ...lane,
    });
    const message: PartStreamingMessage = {
      type: "subscribe_task_deltas",
      task_id: taskId,
      start_id: startId,
      ...lane,
    };
    this.websocket.send(JSON.stringify(message));
  }

  unsubscribeFromTaskDeltas(taskId: string): void {
    this.taskSubscriptions.delete(taskId);
    // Tell the server too — otherwise the subscription idles until the
    // socket closes.
    if (this.websocket.readyState === WebSocket.OPEN) {
      this.websocket.send(
        JSON.stringify({ type: 'unsubscribe_task_deltas', task_id: taskId })
      );
    }
  }

  /** Resume cursor for a task ("0" when unknown). */
  getTaskResumeCursor(taskId: string): string {
    return this.taskSubscriptions.get(taskId)?.lastStreamId ?? "0";
  }

  hasTaskSubscriptions(): boolean {
    return this.taskSubscriptions.size > 0;
  }

  // Register a per-part delta handler. When a task subscription is
  // carrying the deltas, this is purely local (buffered deltas replay
  // immediately); otherwise falls back to the legacy per-part
  // subscribe round-trip.
  async subscribeToPartDeltas(
    partId: string,
    handler: PartDeltaHandler,
    startSequence: number = 0
  ): Promise<void> {
    if (this.websocket.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket is not connected');
    }

    this.deltaHandlers.set(partId, handler);

    const existing = this.subscriptions.get(partId);
    if (!existing) {
      this.subscriptions.set(partId, {
        part_id: partId,
        deltas: [],
        isActive: true,
        currentSequence: startSequence
      });
    } else {
      existing.isActive = true;
      // Replay deltas buffered before the handler registered (task-mode
      // routing buffers by part id).
      for (const event of existing.deltas) {
        handler(event);
      }
    }

    if (this.hasTaskSubscriptions()) {
      // Task stream carries this part's deltas — no per-part round-trip.
      return;
    }

    const message: PartStreamingMessage = {
      type: "subscribe_part_deltas",
      part_id: partId,
      start_sequence: startSequence,
      ...boundBranch(),
    };
    this.websocket.send(JSON.stringify(message));
  }

  // Unsubscribe from part delta streaming
  unsubscribeFromPartDeltas(partId: string): void {
    this.deltaHandlers.delete(partId);
    this.subscriptions.delete(partId);
    // No need to send message - streaming ends automatically when part completes
  }

  // Handle messages (called by external WebSocket message handler)
  handleMessage(message: PartStreamingResponse): void {
    switch (message.type) {
      case 'subscribe_part_deltas_response':
        this.handleSubscriptionResponse(message);
        break;
      case 'subscribe_task_deltas_response':
        this.handleTaskSubscriptionResponse(message);
        break;
      case 'delta':
        this.handlePartDelta(message);
        break;
      case 'resubscribe_required':
        this.handleResubscribeRequired(message);
        break;
      case 'error':
        this.handleError(message.reason ?? 'Live updates for this chat failed.');
        break;
    }
  }

  // Server dropped us as a slow consumer — resubscribe from the last
  // stream id we saw (Redis retains the in-flight part, so the gap
  // replays via catchup).
  private handleResubscribeRequired(message: any): void {
    const taskId = message.task_id;
    if (!taskId) return;
    const existing = this.taskSubscriptions.get(taskId);
    if (!existing) return;
    const cursor = existing.lastStreamId;
    this.taskSubscriptions.delete(taskId);
    try {
      this.subscribeToTaskDeltas(taskId, cursor, existing.branch ?? "");
    } catch {
      // Socket not open — the reconnect path resubscribes anyway.
    }
  }

  private handleSubscriptionResponse(response: any): void {
    const subscription = this.subscriptions.get(response.part_id);
    if (subscription) {
      subscription.currentSequence = response.current_sequence;
      subscription.isActive = true;
      subscription.error = undefined;
    }
  }

  private handleTaskSubscriptionResponse(response: any): void {
    const subscription = this.taskSubscriptions.get(response.task_id);
    if (subscription) {
      subscription.isActive = true;
    }
  }

  private handlePartDelta(event: PartDeltaEvent): void {
    const partId = event.delta.part_id;

    // Advance the owning task's resume cursor.
    const taskId = event.delta.task_id;
    const streamId = event.delta.stream_id;
    if (taskId && streamId) {
      const taskSub = this.taskSubscriptions.get(taskId);
      if (taskSub) {
        taskSub.lastStreamId = streamId;
      }
    }

    let subscription = this.subscriptions.get(partId);
    if (!subscription && this.hasTaskSubscriptions()) {
      // Task-mode: deltas can precede the part renderer's handler
      // registration — buffer so registration can replay.
      subscription = {
        part_id: partId,
        deltas: [],
        isActive: false,
        currentSequence: 0
      };
      this.subscriptions.set(partId, subscription);
    }

    if (subscription) {
      // Per-part sequences are monotonic — drop duplicates from
      // catchup/live overlap or double-delivery during fallback races.
      const sequence = parseInt(event.delta.sequence);
      if (!isNaN(sequence) && sequence <= subscription.currentSequence) {
        return;
      }
      subscription.deltas.push(event);
      if (!isNaN(sequence)) {
        subscription.currentSequence = sequence;
      }
    }

    const handler = this.deltaHandlers.get(partId);
    if (handler) {
      handler(event);
    }
  }

  private handleError(error: string): void {
    this.errorHandlers.forEach(handler => handler(error));
  }

  // Event handler management
  onError(handler: PartStreamingErrorHandler): void {
    this.errorHandlers.add(handler);
  }

  removeErrorHandler(handler: PartStreamingErrorHandler): void {
    this.errorHandlers.delete(handler);
  }

  // Query methods
  getSubscription(partId: string): PartStreamingSubscription | undefined {
    return this.subscriptions.get(partId);
  }

  getDeltas(partId: string): PartDeltaEvent[] {
    const subscription = this.subscriptions.get(partId);
    return subscription ? subscription.deltas : [];
  }

  isSubscribed(partId: string): boolean {
    const subscription = this.subscriptions.get(partId);
    return subscription ? subscription.isActive : false;
  }

  // Clean up completed parts
  cleanupPart(partId: string): void {
    this.unsubscribeFromPartDeltas(partId);
  }
}
